import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Upload, Loader2 } from 'lucide-react'
import {
  getFeedbackImageUrl,
  uploadFeedbackImage,
  deleteFeedbackImage,
  type FeedbackImage,
} from '../../lib/feedbackApi'
import { ImageLightbox } from '../ui/ImageLightbox'

interface FeedbackImageGalleryProps {
  feedbackId: number
  images: FeedbackImage[]
  canEdit?: boolean
  pasteEnabled?: boolean
  onImagesChanged?: () => void
}

const MAX_IMAGES = 3

export function FeedbackImageGallery({
  feedbackId,
  images,
  canEdit,
  pasteEnabled,
  onImagesChanged,
}: FeedbackImageGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const activeImage = lightboxIndex === null ? null : images[lightboxIndex]

  const handleUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) return
    if (images.length >= MAX_IMAGES) return
    setUploading(true)
    try {
      await uploadFeedbackImage(feedbackId, file)
      onImagesChanged?.()
    } catch {
      // Upload failed
    } finally {
      setUploading(false)
    }
  }, [feedbackId, images.length, onImagesChanged])

  const handleDelete = useCallback(async (imageId: number) => {
    setDeletingId(imageId)
    try {
      await deleteFeedbackImage(feedbackId, imageId)
      onImagesChanged?.()
    } catch {
      // Delete failed
    } finally {
      setDeletingId(null)
    }
  }, [feedbackId, onImagesChanged])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    for (const file of Array.from(files)) {
      handleUpload(file)
    }
    e.target.value = ''
  }, [handleUpload])

  // Ctrl+V paste support — only active when pasteEnabled is true
  useEffect(() => {
    if (!canEdit || !pasteEnabled) return
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) handleUpload(file)
          break
        }
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [canEdit, pasteEnabled, handleUpload])

  if (images.length === 0 && !canEdit) return null

  return (
    <>
      <div className="flex gap-2 flex-wrap items-end">
        {images.map((image, i) => (
          <div key={image.id} className="relative group">
            <button
              onClick={() => setLightboxIndex(i)}
              className="w-20 h-20 rounded-lg border border-border overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all shrink-0"
            >
              <img
                src={getFeedbackImageUrl(image.id)}
                alt={image.filename || 'Screenshot'}
                className={`w-full h-full object-cover ${deletingId === image.id ? 'opacity-50' : ''}`}
              />
            </button>
            {canEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(image.id) }}
                disabled={deletingId === image.id}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                title="Remove image"
              >
                {deletingId === image.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <X className="w-3 h-3" />
                )}
              </button>
            )}
          </div>
        ))}

        {canEdit && images.length < MAX_IMAGES && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-lg border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-secondary hover:border-border transition-colors disabled:opacity-50"
            title="Upload image (or Ctrl+V to paste)"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span className="text-[10px]">Add</span>
              </>
            )}
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {canEdit && images.length === 0 && (
        <p className="text-xs text-muted-foreground mt-1">Paste with Ctrl+V or click to upload</p>
      )}

      {activeImage && (
        <ImageLightbox
          src={getFeedbackImageUrl(activeImage.id)}
          alt={activeImage.filename || 'Screenshot'}
          open
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}
